import React, { useState } from 'react'
import { IconButton, Tooltip } from '@mui/material'
import StarOutlined from '@mui/icons-material/StarOutlined'
import StarBorderOutlined from '@mui/icons-material/StarBorderOutlined'
import EditOutlined from '@mui/icons-material/EditOutlined'
import PersonAddAltOutlined from '@mui/icons-material/PersonAddAltOutlined'
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined'
import { SavedAudience, SavedAudienceType } from '@marketing-milk/interfaces'
import { AddCustomerDataModal } from '../../audience-builder/hooks/steps/customer-data/AddCustomerDataModal'
import { toast } from 'react-toastify'

export interface AudienceToolsProps {
  audience: SavedAudience
  onSetDefault?: (audience: SavedAudience, isDefault: boolean) => void
  onDelete?: (audience: SavedAudience) => void
  onEdit?: (audience: SavedAudience) => void
}

type RowActionProps = {
  label: string
  /** When set, the action is unavailable and this is shown in place of the label. */
  disabledReason?: string
  icon: React.ReactNode
  onClick: () => void
}

// A disabled MUI button swallows pointer events, so the tooltip needs a wrapper to listen on —
// otherwise the explanation for the disabled state never appears, which is the point of it.
const RowAction = ({ label, disabledReason, icon, onClick }: RowActionProps) => (
  <Tooltip title={disabledReason ?? label}>
    <span>
      <IconButton
        size="small"
        color="primary"
        aria-label={disabledReason ? `${label} (unavailable): ${disabledReason}` : label}
        disabled={!!disabledReason}
        onClick={onClick}
      >
        {icon}
      </IconButton>
    </span>
  </Tooltip>
)

export function AudienceTools({ audience, onSetDefault, onDelete, onEdit }: AudienceToolsProps) {
  const [customerDataModalOpen, setCustomerDataModalOpen] = useState(false)

  if (!onEdit && !onDelete && !onSetDefault) {
    return null
  }

  // Audiences imported from Facebook are a mirror of something we don't own: the API
  // offers no way to change one, so editing has to happen in Ads Manager.
  const importedReason = audience.createdWithMM
    ? undefined
    : 'This audience was imported from Facebook, so it can only be edited in Ads Manager.'

  return (
    <div className="flex items-center justify-end">
      {onSetDefault && (
        <RowAction
          label={audience.isDefault ? 'Remove as default audience' : 'Set as default audience'}
          icon={
            audience.isDefault ? (
              <StarOutlined fontSize="small" />
            ) : (
              <StarBorderOutlined fontSize="small" />
            )
          }
          onClick={() => onSetDefault(audience, !audience.isDefault)}
        />
      )}
      {onEdit && (
        <RowAction
          label="Edit audience"
          disabledReason={importedReason}
          icon={<EditOutlined fontSize="small" />}
          onClick={() => onEdit(audience)}
        />
      )}
      {audience.type === SavedAudienceType.CustomerFile && (
        <RowAction
          label="Add customer data"
          icon={<PersonAddAltOutlined fontSize="small" />}
          onClick={() => setCustomerDataModalOpen(true)}
        />
      )}
      {onDelete && (
        <RowAction
          label="Delete audience"
          icon={<DeleteOutlineOutlined fontSize="small" />}
          onClick={() => onDelete(audience)}
        />
      )}

      {/* Mounted only while open: the list renders every audience at once, and this carries
          a theme and media-query subscription that would otherwise run once per row. */}
      {customerDataModalOpen && (
        <AddCustomerDataModal
          businessID={audience.businessID}
          audienceID={audience.id}
          isOpen={customerDataModalOpen}
          targetSpec={audience.targeting}
          onClose={toastData => {
            setCustomerDataModalOpen(false)
            if (toastData) {
              toast[toastData.type](toastData.msg)
            }
          }}
        />
      )}
    </div>
  )
}
